/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

// @mui material components
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';

function ProductCost({ parent }) {
  const mod = 'product_cost';
  const app = useSelector((state) => state[APP_STATE]);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [field, setField] = useState({});

  const view = () => {
    setLoading(true);
    FetchData('POST', `/${mod}`, app.token, {
      id: parent.id,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setList(result.list);
        setField(result.parent);
      }
    });
  };

  const open = () => {
    setModalOpen(true);
    view();
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderInfo = () => {
    const costFix = field.cost_fix ?? parent.cost_fix;
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='body2' color='text'>
          ราคาทุนปัจจุบัน
        </MDTypography>
        <MDTypography variant='h5' color='warning'>
          {Func.numberDisplay(costFix, 2)} ฿
        </MDTypography>
      </MDBox>
    );
  };

  const renderList = () => {
    return (
      <MDBox className='card'>
        <TableContainer sx={{ boxShadow: 0, minHeight: 300, maxHeight: 400 }}>
          <Table size='small' aria-label='cost history'>
            <TableHead>
              <TableRow>
                <TableCell align='center'>วันที่</TableCell>
                <TableCell align='center'>ใบรับ</TableCell>
                <TableCell>ผู้ขาย</TableCell>
                <TableCell align='right'>คงเหลือก่อน</TableCell>
                <TableCell align='right'>จำนวนรับ</TableCell>
                <TableCell align='right'>คงเหลือหลัง</TableCell>
                <TableCell align='right'>ทุนรับเข้า</TableCell>
                <TableCell align='right'>ทุนเดิม</TableCell>
                <TableCell align='right'>ทุนใหม่</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell align='center'>{Func.dateTimeDisplay(row.created_at)}</TableCell>
                  <TableCell align='center'>{row.receive_code || '-'}</TableCell>
                  <TableCell>{row.supplier_name || '-'}</TableCell>
                  <TableCell align='right'>
                    {Func.numberDisplay(row.quantity_balance_before, 2)}
                  </TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity_receive, 2)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.quantity_balance, 2)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.cost, 2)}</TableCell>
                  <TableCell align='right'>{Func.numberDisplay(row.cost_fix_before, 2)}</TableCell>
                  <TableCell align='right'>
                    {Func.numberDisplay(row.cost_fix ?? row.cost, 2)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </MDBox>
    );
  };

  const btnOpen = () => {
    return (
      <Tooltip title='ประวัติราคาทุน'>
        <MDButton
          variant='text'
          color='warning'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'inline-block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={open}
        >
          {Func.numberDisplay(parent.cost_fix, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const renderOpen = () => {
    return (
      <Dialog
        open={modalOpen}
        maxWidth='lg'
        keepMounted
        aria-describedby='dialog-cost-history'
        fullWidth
        onClose={() => setModalOpen(false)}
      >
        <DialogTitle>
          <MDBox className='cut'>ประวัติราคาทุน : {parent.code}</MDBox>
          <IconButton
            aria-label='close'
            onClick={() => setModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {renderInfo()}
          {renderList()}
        </DialogContent>
      </Dialog>
    );
  };

  return (
    <div>
      {btnOpen()}
      {renderOpen()}
      {renderLoading()}
    </div>
  );
}

ProductCost.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    code: PropTypes.string.isRequired,
    barcode: PropTypes.string.isRequired,
    cost: PropTypes.string.isRequired,
    cost_fix: PropTypes.string.isRequired,
    cost_sell: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};

export default ProductCost;
